import { useEffect, useId, useState } from 'react';
import type { ReactNode } from 'react';
import { Sheet } from './sheet';
import { useLanguage } from './language-context';

// CB-17 confirm / review dialog (admin design review dialog: from→to diff list,
// required reason, typed guard — VIS §7.2 "Dirty guard + confirm-with-reason").
// Used for moderated and destructive admin actions and by draft-publish-bar.tsx
// to review a publish. The reason is handed to the caller, which sends it with the
// mutation so the server writes it into the audit event (API-26).

export interface ReviewChange {
  label: string;
  from: ReactNode;
  to: ReactNode;
}

export type ConfirmTone = 'accent' | 'danger' | 'warn' | 'ok' | 'dark';

export interface ConfirmWithReasonProps {
  open: boolean;
  onClose: () => void;
  /** Receives the trimmed reason ('' when no reason is asked). May be async. */
  onConfirm: (reason: string) => void | Promise<void>;
  title: string;
  body?: ReactNode;
  changes?: ReviewChange[];
  /** Label for the reason textarea; omit to not ask for a reason. */
  reasonLabel?: string;
  reasonPlaceholder?: string;
  /** Default true when `reasonLabel` is set. */
  reasonRequired?: boolean;
  minReasonLength?: number;
  /** When set, the confirm button stays disabled until this exact text is typed. */
  typedGuard?: string;
  /** Label above the typed-guard input, e.g. "Type DELETE to confirm". */
  typedGuardLabel?: string;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
  width?: number;
}

export function ConfirmWithReason(props: ConfirmWithReasonProps) {
  const {
    open,
    onClose,
    onConfirm,
    title,
    body,
    changes,
    reasonLabel,
    reasonPlaceholder,
    minReasonLength = 1,
    typedGuard,
    typedGuardLabel,
    confirmLabel,
    cancelLabel,
    tone = 'accent',
    width = 480,
  } = props;
  const { t } = useLanguage();
  const reasonRequired = props.reasonRequired ?? reasonLabel !== undefined;
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reasonId = useId();
  const guardId = useId();

  // Fresh form each time the dialog opens.
  useEffect(() => {
    if (open) {
      setReason('');
      setTyped('');
      setBusy(false);
      setError(null);
    }
  }, [open]);

  const reasonOk = !reasonRequired || reason.trim().length >= minReasonLength;
  const guardOk = typedGuard === undefined || typed === typedGuard;
  const canConfirm = reasonOk && guardOk && !busy;

  const submit = async () => {
    if (!canConfirm) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(reason.trim());
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={busy ? () => undefined : onClose}
      title={title}
      width={width}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={busy}>
            {cancelLabel ?? t('admin.action.cancel')}
          </button>
          <button
            type="button"
            className={`btn btn--${tone}`}
            onClick={submit}
            disabled={!canConfirm}
            aria-busy={busy || undefined}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      {body ? <div className="confirm__body">{body}</div> : null}
      {changes && changes.length > 0 ? (
        <ul className="diff-list">
          {changes.map((c, i) => (
            <li key={`${c.label}-${i}`} className="diff-list__row">
              <span className="diff-list__label">{c.label}</span>
              <span className="diff-list__from">{c.from}</span>
              <span className="diff-list__arrow" aria-hidden="true">
                →
              </span>
              <span className="diff-list__to">{c.to}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {reasonLabel !== undefined ? (
        <div className="form-field">
          <label htmlFor={reasonId}>{reasonLabel}</label>
          <textarea
            id={reasonId}
            className="field"
            rows={3}
            value={reason}
            placeholder={reasonPlaceholder}
            required={reasonRequired}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      ) : null}
      {typedGuard !== undefined ? (
        <div className="form-field">
          <label htmlFor={guardId}>{typedGuardLabel ?? typedGuard}</label>
          <input
            id={guardId}
            className="field cell-mono"
            value={typed}
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => setTyped(e.target.value)}
          />
        </div>
      ) : null}
      {error ? (
        <p className="notice notice--err" role="alert">
          {error}
        </p>
      ) : null}
    </Sheet>
  );
}
